// ✅ 모바일 전용 ReferenceModal
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { Search, X, Check, Users } from "lucide-react";
import axios from "../../api/axios";
import { listRoles } from "../../api/users/roles";

const MobileReferenceModal = ({
  isOpen,
  onClose,
  selectedIds = [],
  onChange,
  approvalLines = [],
  onSave = () => { },
  name = "담당자",
  users: usersProp,
  scheduleType,
  companyId,
  single = false,
}) => {
  const safeSelectedIds = Array.isArray(selectedIds) ? selectedIds : [];
  const [users, setUsers] = useState([]);
  const projectId = localStorage.getItem("projectId");
  const [selectedProjectRoles, setSelectedProjectRoles] = useState([]);

  const [keyword, setKeyword] = useState("");
  const [companyFilter, setCompanyFilter] = useState("ALL");

  useEffect(() => {
    if (!isOpen) {
      setKeyword("");
      setCompanyFilter("ALL");
    }
  }, [isOpen]);

  const buildSelectedUserDetails = () =>
    safeSelectedIds
      .map((id) => users.find((u) => u.id === id))
      .filter(Boolean)
      .map(({ id, last_name, first_name, position, company }) => ({
        id,
        last_name,
        first_name,
        position,
        company,
      }));

  const toggleProjectRole = (role) =>
    setSelectedProjectRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );

  useEffect(() => {
    const loadRoles = async () => {
      try {
        await listRoles({ project: projectId, page_size: 1000 });
      } catch (e) {
        console.error("Failed to load roles", e);
      }
    };
    if (isOpen) loadRoles();
  }, [projectId, isOpen]);

  const usersPropKey = useMemo(() => {
    if (!Array.isArray(usersProp) || usersProp.length === 0) return "";
    return usersProp.map((u) => u.id).join(",");
  }, [usersProp]);

  useEffect(() => {
    if (!isOpen) return;

    if (Array.isArray(usersProp) && usersProp.length > 0) {
      let list = usersProp;
      if (scheduleType === "company" && companyId) {
        list = list.filter((u) => Number(u.company_id) === Number(companyId));
      }
      setUsers((prev) => {
        if (prev.length === list.length && prev.every((u, i) => u === list[i])) {
          return prev;
        }
        return list;
      });
      return;
    }

    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`/project/${projectId}/users-without-me/`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("access")}` },
        });
        if (cancelled) return;

        let list = Array.isArray(res.data?.results)
          ? res.data.results
          : res.data ?? [];

        if (scheduleType === "company" && companyId) {
          list = list.filter((u) => Number(u.company_id) === Number(companyId));
        }
        setUsers(list);
      } catch (e) {
        if (!cancelled) console.error("Failed to load users", e);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [isOpen, usersPropKey, projectId, scheduleType, companyId]);

  const approvalIdsKey = useMemo(
    () =>
      (approvalLines || [])
        .map((a) => String(a.approver_id ?? a.id ?? ""))
        .join(","),
    [approvalLines]
  );

  const referenceList = useMemo(() => {
    if (!isOpen) return [];

    const approvalIds = new Set(
      (approvalLines || []).map((a) => String(a.approver_id ?? a.id))
    );

    let list = users.filter((u) => !approvalIds.has(String(u.id)));

    if (scheduleType === "company" && companyId) {
      list = list.filter((u) => Number(u.company_id) === Number(companyId));
    }

    if (selectedProjectRoles.length > 0) {
      list = list.filter((u) =>
        (u.project_role || []).some((r) => selectedProjectRoles.includes(r))
      );
    }

    return list;
  }, [isOpen, users, selectedProjectRoles, scheduleType, companyId, approvalIdsKey, approvalLines]);

  const groupedByCompany = useMemo(() => {
    const map = new Map();
    referenceList.forEach((u) => {
      const company = u.company || "회사 미지정";
      const dept = u.department || "부서 미지정";
      if (!map.has(company)) map.set(company, new Map());
      const deptMap = map.get(company);
      if (!deptMap.has(dept)) deptMap.set(dept, []);
      deptMap.get(dept).push(u);
    });
    return map;
  }, [referenceList]);

  const companyNames = useMemo(() => Array.from(groupedByCompany.keys()), [groupedByCompany]);

  const trimmedKeyword = keyword.trim().toLowerCase();

  const matchKeyword = useCallback(
    (user) => {
      if (!trimmedKeyword) return true;
      const userName = `${user.last_name || ""}${user.first_name || ""}`;
      const text = [userName, user.company, user.department, user.position]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return text.includes(trimmedKeyword);
    },
    [trimmedKeyword]
  );

  const handleToggle = (id) => {
    const numId = Number(id);
    if (single) {
      onChange([numId]);
    } else {
      if (safeSelectedIds.includes(numId)) {
        onChange(safeSelectedIds.filter((v) => v !== numId));
      } else {
        onChange([...safeSelectedIds, numId]);
      }
    }
  };

  const handleToggleCompanyAll = (company, usersInCompany) => {
    const idsInCompany = usersInCompany.map((u) => Number(u.id));
    const hasAll = idsInCompany.every((id) => safeSelectedIds.includes(id));

    if (hasAll) {
      onChange(safeSelectedIds.filter((id) => !idsInCompany.includes(id)));
    } else {
      const next = new Set(safeSelectedIds);
      idsInCompany.forEach((id) => next.add(id));
      onChange(Array.from(next));
    }
  };

  const projectRoles = useMemo(
    () => [...new Set(users.flatMap((u) => u.project_role || []))],
    [users]
  );

  if (!isOpen) return null;

  const modal = (
    <div className="fixed inset-0 z-[1200] flex items-end justify-center">
      {/* 백드롭 */}
      <button
        type="button"
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        aria-label={`${name} 선택 닫기`}
      />

      {/* 바텀시트 - 고정 높이로 들쭉날쭉 방지 */}
      <div className="relative w-full max-w-md mx-auto rounded-t-3xl bg-white shadow-2xl h-[85vh] flex flex-col animate-slide-up">
        {/* 핸들 바 */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="h-1 w-10 rounded-full bg-slate-300" />
        </div>

        {/* 헤더 */}
        <div className="px-5 pb-3 flex items-center justify-between border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
              <Users size={16} className="text-slate-600" />
            </div>
            <h2 className="text-[18px] font-bold text-slate-900">{name} 선택</h2>
          </div>
          <span className="text-[13px] text-slate-500 font-medium">
            {safeSelectedIds.length > 0 ? `${safeSelectedIds.length}명 선택` : "선택 없음"}
          </span>
        </div>

        {/* 검색 */}
        <div className="px-4 pt-3">
          <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
            <Search size={16} className="text-slate-400 flex-shrink-0" />
            <input
              className="flex-1 bg-transparent text-[14px] text-slate-900 outline-none placeholder:text-slate-400"
              placeholder="이름, 회사, 부서로 검색"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
            {keyword && (
              <button onClick={() => setKeyword("")} className="text-slate-400">
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {/* 역할 필터 - 세그먼트 컨트롤 */}
        {projectRoles.length > 0 && (
          <div className="px-4 pt-3">
            <div className="flex rounded-xl bg-slate-100 p-1 overflow-x-auto scrollbar-hide">
              <button
                onClick={() => setSelectedProjectRoles([])}
                className={`flex-shrink-0 rounded-lg px-3 py-1.5 text-[12px] font-medium transition-all ${selectedProjectRoles.length === 0
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-500"
                  }`}
              >
                전체
              </button>
              {projectRoles.map((role) => (
                <button
                  key={role}
                  onClick={() => toggleProjectRole(role)}
                  className={`flex-shrink-0 rounded-lg px-3 py-1.5 text-[12px] font-medium transition-all whitespace-nowrap ${selectedProjectRoles.includes(role)
                      ? "bg-white text-slate-900 shadow-sm"
                      : "text-slate-500"
                    }`}
                >
                  {role}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* 회사 필터 */}
        {companyNames.length > 1 && (
          <div className="px-4 pt-2">
            <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-1">
              <button
                type="button"
                onClick={() => setCompanyFilter("ALL")}
                className={`flex-shrink-0 rounded-full px-3 py-1.5 text-[12px] font-medium transition-all ${companyFilter === "ALL"
                    ? "bg-slate-900 text-white"
                    : "bg-white border border-slate-200 text-slate-600"
                  }`}
              >
                전체
              </button>
              {companyNames.map((compName) => (
                <button
                  key={compName}
                  type="button"
                  onClick={() => setCompanyFilter(compName)}
                  className={`flex-shrink-0 rounded-full px-3 py-1.5 text-[12px] font-medium transition-all whitespace-nowrap ${companyFilter === compName
                      ? "bg-slate-900 text-white"
                      : "bg-white border border-slate-200 text-slate-600"
                    }`}
                >
                  {compName}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* 선택된 사람 칩 */}
        {safeSelectedIds.length > 0 && (
          <div className="px-4 pt-2">
            <div className="flex flex-wrap gap-1.5">
              {safeSelectedIds
                .map((id) => users.find((u) => Number(u.id) === id))
                .filter(Boolean)
                .map((user) => {
                  const userName = `${user.last_name || ""}${user.first_name || ""}`;
                  return (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => handleToggle(user.id)}
                      className="flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-[12px] font-medium text-blue-700 active:scale-95 transition-transform"
                    >
                      <span>{userName}</span>
                      <X size={12} className="text-blue-500" />
                    </button>
                  );
                })}
            </div>
          </div>
        )}

        {/* 목록 영역 */}
        <div className="mt-3 flex-1 overflow-y-auto px-4 pb-3 space-y-3">
          {companyNames.length === 0 && (
            <div className="py-12 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-slate-100 flex items-center justify-center">
                <Users size={24} className="text-slate-400" />
              </div>
              <p className="text-[14px] text-slate-400">선택할 수 있는 사용자가 없습니다</p>
            </div>
          )}

          {companyNames.map((companyName) => {
            if (companyFilter !== "ALL" && companyFilter !== companyName) return null;

            const deptMap = groupedByCompany.get(companyName);
            if (!deptMap) return null;

            const usersInCompany = Array.from(deptMap.values()).flat();
            const visibleUsers = usersInCompany.filter((u) => matchKeyword(u));
            if (visibleUsers.length === 0) return null;

            const allSelected = usersInCompany.every((u) =>
              safeSelectedIds.includes(Number(u.id))
            );

            return (
              <div key={companyName} className="rounded-2xl bg-slate-50 overflow-hidden">
                {/* 회사 헤더 */}
                <div className="flex items-center justify-between px-4 py-2.5 bg-slate-100/50">
                  <span className="text-[13px] font-semibold text-slate-700">{companyName}</span>
                  <button
                    type="button"
                    onClick={() => handleToggleCompanyAll(companyName, usersInCompany)}
                    className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-white border border-slate-200 text-slate-600 active:scale-95 transition-transform"
                  >
                    {allSelected ? "전체 해제" : "전체 선택"}
                  </button>
                </div>

                {/* 부서별 리스트 */}
                <div className="px-2 py-2 space-y-2">
                  {Array.from(deptMap.entries()).map(([deptName, deptUsers]) => {
                    const filteredDeptUsers = deptUsers.filter((u) => matchKeyword(u));
                    if (filteredDeptUsers.length === 0) return null;

                    return (
                      <div key={deptName}>
                        <div className="text-[11px] font-medium text-slate-500 px-2 mb-1">{deptName}</div>
                        <div className="space-y-1">
                          {filteredDeptUsers.map((user) => {
                            const idNum = Number(user.id);
                            const selected = safeSelectedIds.includes(idNum);
                            const userName = `${user.last_name || ""}${user.first_name || ""}`;
                            const meta = user.position || "";

                            return (
                              <button
                                key={user.id}
                                type="button"
                                onClick={() => handleToggle(user.id)}
                                className={`w-full flex items-center justify-between rounded-xl px-3 py-2.5 text-left transition-all active:scale-[0.98] ${selected ? "bg-blue-50 border border-blue-200" : "bg-white border border-transparent"
                                  }`}
                              >
                                <div>
                                  <p className={`text-[14px] font-medium ${selected ? "text-blue-900" : "text-slate-900"}`}>
                                    {userName}
                                  </p>
                                  {meta && (
                                    <p className={`text-[12px] ${selected ? "text-blue-600" : "text-slate-500"}`}>
                                      {meta}
                                    </p>
                                  )}
                                </div>
                                <div
                                  className={`w-6 h-6 rounded-full flex items-center justify-center transition-all ${selected
                                      ? "bg-blue-600"
                                      : "border-2 border-slate-300"
                                    }`}
                                >
                                  {selected && <Check size={14} className="text-white" strokeWidth={3} />}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* 하단 버튼 */}
        <div className="px-4 py-3 border-t border-slate-200 bg-white/95 backdrop-blur-sm flex gap-3 safe-area-bottom">
          <button
            onClick={onClose}
            className="flex-1 h-12 rounded-xl bg-slate-100 text-[15px] font-semibold text-slate-700 active:scale-[0.98] transition-transform"
          >
            닫기
          </button>
          <button
            onClick={async () => {
              const ok = await onSave([...safeSelectedIds], buildSelectedUserDetails());
              if (ok !== false) onClose();
            }}
            className="flex-1 h-12 rounded-xl bg-blue-600 text-[15px] font-semibold text-white active:scale-[0.98] transition-transform shadow-lg shadow-blue-600/25"
          >
            저장
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default MobileReferenceModal;
